import React, { useContext, useState } from "react";
import "../../styles/contentMasteryCard.css";
import ContentComponent from "../common/ContentComponent";
import booksImage from "../../images/content_Image.png";
import { WebsiteContext } from "../../context/AppContext";
import { FaFilePdf, FaBookOpen } from "react-icons/fa6";
import { TbVocabulary } from "react-icons/tb";
import { RiCompassDiscoverLine, RiSpeakLine } from "react-icons/ri";
import { LuRefreshCw } from "react-icons/lu";
import { HiSparkles } from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";

const masteryData = {
  pdf: [
    {
      id: "pdf-1",
      category: "PDF Curriculums",
      title: "Fundamentals to Fluency Handbook",
      level: "A1 to C2 Complete Guide",
      meaning:
        "Comprehensive 120+ page master curriculum covering daily conversational structures, essential dialogue templates, and common pronunciation pitfalls.",
      example:
        "Chapter 4: Navigating Workplace Conversations, Polite Suggestions & Executive Dialogue.",
      badge: "128 Pages • High-Res PDF",
      extraBadge: "Audio Drills Included",
    },
    {
      id: "pdf-2",
      category: "PDF Curriculums",
      title: "Tenses & Timelines Cheat Sheet",
      level: "Beginner to Intermediate",
      meaning:
        "Visual roadmaps for all 12 English tenses with timeline diagrams, formula tables, and real-life speaking examples to eliminate hesitation.",
      example:
        "Never confuse Present Perfect with Simple Past again ('I have completed' vs 'I completed').",
      badge: "36 Pages • Printable PDF",
      extraBadge: "Timeline Charts",
    },
  ],
  idioms: [
    {
      id: "idiom-1",
      category: "Everyday Idioms",
      title: "Bite the Bullet",
      level: "Intermediate Fluency",
      meaning:
        "To force yourself to do something difficult, unpleasant, or daunting that you have been hesitating to begin.",
      example:
        "“I was anxious about the client presentation, but I decided to bite the bullet and speak up.”",
      badge: "Idioms PDF Vol. 1",
      extraBadge: "Native Nuance",
    },
    {
      id: "idiom-2",
      category: "Everyday Idioms",
      title: "Through Thick and Thin",
      level: "Casual & Conversational",
      meaning:
        "Remaining loyal, steadfast, and supportive through all circumstances, whether favorable or difficult.",
      example:
        "“True language partners support each other through thick and thin until both achieve fluency.”",
      badge: "Idioms PDF Vol. 2",
      extraBadge: "Practical Example",
    },
  ],
  phrasal: [
    {
      id: "phrasal-1",
      category: "Phrasal Verbs",
      title: "Figure Out",
      level: "Essential Daily",
      meaning:
        "To discover, understand, or resolve a complex matter through careful reasoning and exploration.",
      example:
        "“Once we sat down and discussed it together, we figured out the exact solution.”",
      badge: "Phrasal Drill #18",
      extraBadge: "Workplace Context",
    },
    {
      id: "phrasal-2",
      category: "Phrasal Verbs",
      title: "Bring Up",
      level: "Professional English",
      meaning:
        "To mention, introduce, or initiate discussion regarding a particular topic or observation.",
      example:
        "“Don't hesitate to bring up your feedback during tomorrow's team sync.”",
      badge: "Phrasal Drill #24",
      extraBadge: "Executive Speech",
    },
  ],
  grammar: [
    {
      id: "grammar-1",
      category: "Grammar Vault",
      title: "Conditional 'If' Clauses",
      level: "Intermediate - Advanced",
      meaning:
        "Master Zero, First, Second, and Third conditionals to express hypothetical ideas, future realities, and past reflections effortlessly.",
      example:
        "“If I practice speaking every single day, my confidence will grow exponentially.” (1st Conditional)",
      badge: "Grammar PDF Guide",
      extraBadge: "Structure Breakdown",
    },
    {
      id: "grammar-2",
      category: "Grammar Vault",
      title: "Active vs. Passive Voice",
      level: "Clear Articulation",
      meaning:
        "Understand when to emphasize the doer versus the action itself, essential for crisp workplace communication and writing.",
      example:
        "Active: 'Our mentor explained the idiom clearly.' • Passive: 'The idiom was clearly explained.'",
      badge: "Grammar PDF Guide",
      extraBadge: "Formula Rules",
    },
  ],
};

const ContentOne = () => {
  const { isMobileView, dark } = useContext(WebsiteContext);
  const [activeCategory, setActiveCategory] = useState("pdf");
  const [itemIndex, setItemIndex] = useState(0);

  const currentList = masteryData[activeCategory];
  const currentItem = currentList[itemIndex % currentList.length];

  const handleTabChange = (key) => {
    setActiveCategory(key);
    setItemIndex(0);
  };

  const handleNext = () => {
    setItemIndex((prev) => (prev + 1) % currentList.length);
  };

  return (
    <div className={isMobileView ? "mastery_section_mobile" : "mastery_section"}>
      {/* Description on the left */}
      <div className="mastery_text_wrapper">
        <ContentComponent
          title={"Comprehensive English Mastery: From Fundamentals to Fluency"}
          content={
            "Discover the full spectrum of English language learning with our meticulously crafted PDFs, designed to guide you from foundational basics to advanced proficiency"
          }
        />
      </div>

      {/* 560px Interactive Showcase Card on the right */}
      <div className="mastery_card_wrapper">
        <img src={booksImage} alt="3D Books Stack" className="mastery_3d_badge" />

        <div className={`mastery_card ${dark ? "mastery_card_dark" : "mastery_card_light"}`}>
          {/* Top Bar */}
          <div className="mastery_top_bar">
            <span className="mastery_status_chip">
              <HiSparkles size={13} /> Learning Hub
            </span>
            <span className="mastery_count_badge">
              <FaBookOpen size={12} /> 100+ PDF Guides Available
            </span>
          </div>

          {/* Category Tabs */}
          <div className="mastery_tabs">
            <button
              className={`mastery_tab_btn ${activeCategory === "pdf" ? "mastery_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("pdf")}
            >
              <FaFilePdf size={14} />
              <span>PDF Guides</span>
            </button>
            <button
              className={`mastery_tab_btn ${activeCategory === "idioms" ? "mastery_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("idioms")}
            >
              <TbVocabulary size={15} />
              <span>Idioms</span>
            </button>
            <button
              className={`mastery_tab_btn ${activeCategory === "phrasal" ? "mastery_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("phrasal")}
            >
              <RiCompassDiscoverLine size={15} />
              <span>Phrasal</span>
            </button>
            <button
              className={`mastery_tab_btn ${activeCategory === "grammar" ? "mastery_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("grammar")}
            >
              <RiSpeakLine size={15} />
              <span>Grammar</span>
            </button>
          </div>

          {/* Dynamic Content Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentItem.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <div className="mastery_content_box">
                <div className="mastery_title_row">
                  <span className="mastery_item_title">{currentItem.title}</span>
                  <span className="mastery_level_tag">{currentItem.level}</span>
                </div>

                <p className="mastery_meaning_text">{currentItem.meaning}</p>

                <div className="mastery_example_box">
                  <div className="mastery_example_label">Contextual Example:</div>
                  <div className="mastery_example_text">{currentItem.example}</div>
                </div>

                <div className="mastery_feature_chips">
                  <span className="mastery_feature_chip mastery_pdf_badge">
                    <FaFilePdf size={12} /> {currentItem.badge}
                  </span>
                  <span className="mastery_feature_chip">
                    <HiSparkles size={12} /> {currentItem.extraBadge}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer Bar */}
          <div className="mastery_footer_bar">
            <span className="mastery_hint">Included in Fluent Fable Library</span>
            <button className="mastery_cycle_btn" onClick={handleNext} title="Next resource">
              <LuRefreshCw size={12} /> Next Topic
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentOne;
